import { getCaRTT } from './authUtils';
import CodeError from './CodeError';

let renewTokenCallback: (() => Promise<void>) | null = null;

export function setRenewTokenCallback(callback: () => Promise<void>) {
  renewTokenCallback = callback;
}

function setTokenOption(options: any) {
  const headers = options.headers || {};

  return {
    ...options,
    headers: {
      ...headers,
      Authorization: `Bearer ${getCaRTT()}`,
    },
  };
}

let isRenewing = false;
let renewPromise: Promise<void> | null = null;

export async function authorizedFetch(url: string, options = {}) {
  const updatedOptions = setTokenOption(options);
  const response = await fetch(url, updatedOptions);

  if (response.status === 401 && renewTokenCallback && !isRenewing) {
    console.log('[authFetch] Token expired (401), attempting renewal...'); // eslint-disable-line no-console

    if (!renewPromise) {
      isRenewing = true;
      renewPromise = renewTokenCallback()
        .then(() => {
          console.log('[authFetch] Token renewed, retrying request...'); // eslint-disable-line no-console
        })
        .catch((error) => {
          console.error('[authFetch] Token renewal failed:', error); // eslint-disable-line no-console
          throw error;
        })
        .finally(() => {
          isRenewing = false;
          renewPromise = null;
        });
    }

    await renewPromise;

    const retryOptions = setTokenOption(options);

    return fetch(url, retryOptions);
  }

  return response;
}

export function authorizedUpload(url: string, file: File, onUploadProgress: (event: ProgressEvent) => void) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.onprogress = onUploadProgress;
    xhr.setRequestHeader('Authorization', `Bearer ${getCaRTT()}`);

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.response);
        } else if (xhr.status === 409) {
          reject(new CodeError(xhr.statusText, xhr.status.toString(), xhr.status, xhr.response));
        } else {
          reject(new CodeError(xhr.statusText, xhr.status.toString(), xhr.status, xhr.response));
        }
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network Error'));
    };

    xhr.send(file);
  });
}

const fetchConfig = (method: string, data: any, signal: AbortSignal, customHeaders: any = {}) => ({
  method,
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
    ...customHeaders,
  },
  signal,
});

export const fetchPOST = (url: string, data: any, signal: AbortSignal, customHeaders: any) => (
  authorizedFetch(url, fetchConfig('POST', data, signal, customHeaders)));

export const fetchPUT = (url: string, data: any, signal: AbortSignal, customHeaders: any) => (
  authorizedFetch(url, fetchConfig('PUT', data, signal, customHeaders)));

export const fetchDELETE = (url: string, data: any, signal: AbortSignal, customHeaders: any) => (
  authorizedFetch(url, fetchConfig('DELETE', data, signal, customHeaders)));
